import * as React from 'react';
import './SearchableFieldsPanel.css';
import { PrimaryButton, PanelType, SearchBox, Overlay } from 'office-ui-fabric-react';
import FieldPanel, { IFieldActions } from '../../FieldPanel';
import Spinner from '../../../common/Spinner';
import ExpandablePanel from '../../../common/ExpandablePanel';
import { GroupedFieldsConfigurationProps } from '../GroupedFieldsConfiguration';
import * as Metadata from '../../../../entities/Metadata';

type Props = Omit<GroupedFieldsConfigurationProps, 'fields' | 'selected' | 'mandatoryFields' | 'flattenSelectedFieldGroups' | 'flattenAvailableFieldGroups'> & {
    children: (filter: string, onEditField: (field?: Metadata.Field) => void) => React.ReactNode;
};

const SearchableFieldsPanel = (props: Props) => {
    const [showNewFieldPanel, setShowNewFieldPanel] = React.useState(false);
    const [editField, setEditField] = React.useState<Metadata.Field | undefined>();
    const onEditField = React.useCallback((field?: Metadata.Field) => setEditField(field), []);
    const onAddFieldClick = React.useCallback(() => setShowNewFieldPanel(true), []);
    const hideFieldPanel = React.useCallback(() => {
        setShowNewFieldPanel(false);
        setEditField(undefined);
    }, []);

    const [filter, setFilter] = React.useState('');
    const onFilterChange = React.useCallback((e: any, value?: string) => setFilter((value || '')), []);
    const clearFilter = React.useCallback(() => setFilter(''), []);
    const showEditPanel = (showNewFieldPanel || editField) && props.fieldActions;

    const onDismiss = React.useCallback(() => !showEditPanel && props.onDismiss(), [showEditPanel]);

    const onRenderHeader = React.useCallback((): JSX.Element | null => {
        return <div className="ms-Panel-header">
            <p className="ms-Panel-headerText">{props.headerText || 'Configure Columns'}</p>
            <div className='ms-Panel-secondaryText'>{props.secondaryText || 'Select the columns to be displayed in the view. Drag and drop to reorder.'}</div>
            <SearchBox
                autoFocus
                value={filter}
                placeholder='Search'
                onChange={onFilterChange}
                onClear={clearFilter}
                onEscape={clearFilter}
            />
        </div>;
    }, [props.headerText, props.secondaryText, filter]);

    const onRenderFooter = React.useCallback(() => !props.hideNewFieldButton && props.allowManageFields !== undefined
        ? (
            <PrimaryButton
                iconProps={{ iconName: 'Add' }}
                text="New Field"
                className="new-button"
                disabled={props.showSpinner || !props.allowManageFields}
                onClick={onAddFieldClick}
            />
        ) : null,
        [props.hideNewFieldButton, props.allowManageFields, props.showSpinner]);

    const fieldActions: IFieldActions = React.useMemo(() => ({
        ...props.fieldActions,
        saveField: props.fieldActions
            ? _ => {
                clearFilter();
                props.fieldActions!.saveField?.(_);
            }
            : undefined
    }), [props.fieldActions]);

    return <>
        <ExpandablePanel
            className={`searchable-fields-panel ${showEditPanel ? 'hidden' : ''}`}
            isOpen
            isLightDismiss={!props.showSpinner}
            type={PanelType.custom}
            customWidth="600px"
            onRenderHeader={onRenderHeader}
            onRenderFooterContent={onRenderFooter}
            focusTrapZoneProps={{ disabled: true }}
            onDismiss={onDismiss}
        >
            {props.children(filter, onEditField)}
            {props.showSpinner && <Overlay><Spinner /></Overlay>}
        </ExpandablePanel>
        {showEditPanel && <FieldPanel
            allowManageFields={props.allowManageFields}
            actions={fieldActions}
            field={editField}
            onDismiss={hideFieldPanel}
            entityType={props.entityType} />
        }
    </>
}

export default SearchableFieldsPanel;
